import { ChevronRight, ImageOff } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Callout } from "./_primitives/callout";
import { DisplayNumeral } from "./_primitives/display-numeral";
import { Eyebrow } from "./_primitives/eyebrow";
import { SectionHero } from "./_primitives/section-hero";
import { TwoColumn } from "./_primitives/two-column";
import { asArray, asObject, asString, type RenderProps } from "./render-types";

const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;

function InlineText({ text }: { text: string }): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(LINK_RE)) {
    const idx = match.index ?? 0;
    if (idx > lastIndex) {
      parts.push(text.slice(lastIndex, idx));
    }
    parts.push(
      <a
        key={idx}
        href={match[2]}
        target="_blank"
        rel="noreferrer noopener"
        className="text-primary underline underline-offset-2 hover:text-primary/80"
      >
        {match[1]}
      </a>,
    );
    lastIndex = idx + match[0].length;
  }

  if (parts.length === 0) return text;
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

// rich_doc is the single visual renderType. The block array drives the layout.
// Unknown block types fall back to paragraph so legacy content still renders.

export function RichDoc({ contentBlocks }: RenderProps) {
  if (contentBlocks.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
        Documento vazio.
      </div>
    );
  }
  return (
    <article className="space-y-6 text-sm leading-relaxed">
      {contentBlocks.map((raw, idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: authored content; order is stable
        <Block key={idx} raw={raw} index={idx} />
      ))}
    </article>
  );
}

function Block({ raw, index }: { raw: unknown; index: number }) {
  const block = asObject(raw);
  const type = asString(block.type, "paragraph");

  switch (type) {
    case "hero":
      return (
        <SectionHero
          eyebrow={asString(block.eyebrow)}
          headline={asString(block.headline) || asString(block.title)}
          body={asString(block.body) || asString(block.text)}
        />
      );

    case "heading": {
      const level = typeof block.level === "number" ? block.level : 2;
      const text = asString(block.text);
      const className =
        level === 1
          ? "text-2xl font-semibold mt-6"
          : level === 2
            ? "text-xl font-semibold mt-5"
            : "text-base font-semibold mt-4";
      return <h2 className={className}>{text}</h2>;
    }

    case "paragraph":
      return asString(block.text) ? (
        <p>
          <InlineText text={asString(block.text)} />
        </p>
      ) : null;

    case "bullet_list": {
      const items = asArray(block.items).map((it) => asString(it));
      return (
        <ul className="list-disc pl-5 space-y-1">
          {items.map((it, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: bullet item with no stable id
            <li key={`${index}-${i}`}>
              <InlineText text={it} />
            </li>
          ))}
        </ul>
      );
    }

    case "ordered_list": {
      const items = asArray(block.items).map((it) => asString(it));
      return (
        <ol className="list-decimal pl-5 space-y-1 marker:text-muted-foreground">
          {items.map((it, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: ordered item with no stable id
            <li key={`${index}-${i}`}>
              <InlineText text={it} />
            </li>
          ))}
        </ol>
      );
    }

    case "callout":
    case "quote":
      return (
        <Callout
          variant={
            type === "quote"
              ? "quote"
              : (asString(block.variant, "info") as "info" | "success" | "warn" | "quote")
          }
          title={asString(block.title) || undefined}
        >
          <InlineText text={asString(block.text)} />
        </Callout>
      );

    case "divider":
      return <hr className="border-border" />;

    case "image": {
      const src = asString(block.src);
      const alt = asString(block.alt);
      const caption = asString(block.caption);
      if (!src) return null;
      return <NotionImage src={src} alt={alt} caption={caption} />;
    }

    case "two_column": {
      const left = asArray(block.left);
      const right = asArray(block.right);
      return (
        <TwoColumn
          left={left.map((b, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: nested authored content
            <Block key={`${index}-l-${i}`} raw={b} index={i} />
          ))}
          right={right.map((b, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: nested authored content
            <Block key={`${index}-r-${i}`} raw={b} index={i} />
          ))}
        />
      );
    }

    case "numbered_principles":
      return <NumberedPrinciples items={asArray(block.items)} />;

    case "numbered_list":
      return <NumberedList items={asArray(block.items)} />;

    case "card_grid": {
      const variant = asString(block.variant, "metrics");
      const items = asArray(block.items);
      if (variant === "people") return <PeopleCardGrid items={items} />;
      if (variant === "phase") return <PhaseCardGrid items={items} />;
      return <MetricsCardGrid items={items} />;
    }

    default:
      return asString(block.text) ? (
        <p>
          <InlineText text={asString(block.text)} />
        </p>
      ) : null;
  }
}

function NotionImage({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  const [failed, setFailed] = useState(false);
  const onError = useCallback(() => setFailed(true), []);

  if (failed) {
    return (
      <figure className="space-y-2">
        <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/30 py-10 text-sm text-muted-foreground">
          <ImageOff className="h-4 w-4 shrink-0" />
          <span>Imagem indisponível — clique em "Sincronizar agora" para atualizar</span>
        </div>
        {caption && (
          <figcaption className="text-xs text-muted-foreground text-center">{caption}</figcaption>
        )}
      </figure>
    );
  }

  return (
    <figure className="space-y-2">
      <img src={src} alt={alt} className="w-full rounded-lg border" onError={onError} />
      {caption && (
        <figcaption className="text-xs text-muted-foreground text-center">{caption}</figcaption>
      )}
    </figure>
  );
}

function NumberedPrinciples({ items }: { items: unknown[] }) {
  if (items.length === 0) return null;
  return (
    <ol className="space-y-3">
      {items.map((raw, idx) => {
        const item = asObject(raw);
        return (
          <PrincipleItem
            // biome-ignore lint/suspicious/noArrayIndexKey: authored content; order is stable
            key={idx}
            index={idx}
            title={asString(item.title)}
            subtitle={asString(item.subtitle)}
            summary={asString(item.summary) || asString(item.description)}
            body={asString(item.body)}
          />
        );
      })}
    </ol>
  );
}

function PrincipleItem({
  index,
  title,
  subtitle,
  summary,
  body,
}: {
  index: number;
  title: string;
  subtitle: string;
  summary: string;
  body: string;
}) {
  const [open, setOpen] = useState(false);
  const hasBody = body.length > 0;
  const heading = (
    <div className="flex flex-1 min-w-0 items-start gap-5">
      <DisplayNumeral index={index} className="mt-1" />
      <div className="flex-1 min-w-0 text-left">
        {subtitle && <Eyebrow className="mb-1 text-muted-foreground">{subtitle}</Eyebrow>}
        <div className="text-base font-semibold tracking-tight">
          {title || `Princípio ${index + 1}`}
        </div>
        {summary && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{summary}</p>}
      </div>
    </div>
  );

  if (!hasBody) {
    return <li className="flex gap-2 rounded-lg border bg-card p-5">{heading}</li>;
  }

  return (
    <li className="rounded-lg border bg-card">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-start gap-2 p-5 text-left hover:bg-accent/40 rounded-lg"
          >
            {heading}
            <ChevronRight
              className={cn(
                "h-4 w-4 mt-2 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-90",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border px-5 pb-5 pt-3 pl-[4.25rem]">
            <PrincipleBody body={body} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}

// Light markdown-ish parsing: blank lines split paragraphs, and lines starting
// with "- " or "* " become a bullet list. Keeps the schema simple for LLM
// authors while still reading well in the portal.
function PrincipleBody({ body }: { body: string }) {
  const lines = body.split("\n");
  type Block = { kind: "p"; text: string } | { kind: "ul"; items: string[] };
  const blocks: Block[] = [];
  let buffer: string[] = [];
  let bullets: string[] = [];

  const flushParagraph = () => {
    if (buffer.length === 0) return;
    blocks.push({ kind: "p", text: buffer.join(" ").trim() });
    buffer = [];
  };
  const flushBullets = () => {
    if (bullets.length === 0) return;
    blocks.push({ kind: "ul", items: bullets });
    bullets = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line.length === 0) {
      flushParagraph();
      flushBullets();
      continue;
    }
    const bulletMatch = /^[-*]\s+(.*)$/.exec(line);
    if (bulletMatch) {
      flushParagraph();
      bullets.push(bulletMatch[1] ?? "");
    } else {
      flushBullets();
      buffer.push(line);
    }
  }
  flushParagraph();
  flushBullets();

  return (
    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
      {blocks.map((block, idx) => {
        if (block.kind === "ul") {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: parsed in stable order
            <ul key={idx} className="list-disc pl-5 space-y-1">
              {block.items.map((item, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: bullet item with no stable id
                <li key={`${idx}-${i}`}>
                  <InlineText text={item} />
                </li>
              ))}
            </ul>
          );
        }
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: parsed in stable order
          <p key={idx}>
            <InlineText text={block.text} />
          </p>
        );
      })}
    </div>
  );
}

function NumberedList({ items }: { items: unknown[] }) {
  if (items.length === 0) return null;
  return (
    <ol className="space-y-4">
      {items.map((raw, idx) => {
        const item = asObject(raw);
        const title = asString(item.title);
        const description = asString(item.description);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: authored content; order is stable
          <li key={idx} className="flex items-start gap-5 rounded-lg border bg-card p-5">
            <DisplayNumeral index={idx} className="mt-1" />
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold tracking-tight">
                {title || `Item ${idx + 1}`}
              </div>
              {description && (
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{description}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
}

function PeopleCardGrid({ items }: { items: unknown[] }) {
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((raw, idx) => {
        const item = asObject(raw);
        const name = asString(item.name);
        const role = asString(item.role);
        const bio = asString(item.bio);
        const avatarUrl = asString(item.avatarUrl);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: authored content; order is stable
          <Card key={idx} className="p-4 flex items-start gap-3">
            <Avatar className="h-10 w-10">
              {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                {initials(name) || "??"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{name || "—"}</div>
              {role && <div className="text-xs text-muted-foreground truncate">{role}</div>}
              {bio && <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{bio}</p>}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function MetricsCardGrid({ items }: { items: unknown[] }) {
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((raw, idx) => {
        const item = asObject(raw);
        const label = asString(item.label);
        const value = asString(item.value);
        const unit = asString(item.unit);
        const trend = asString(item.trend);
        const direction = asString(item.direction);
        const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "";
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: authored content; order is stable
          <Card key={idx} className="p-5 space-y-2">
            <Eyebrow className="text-muted-foreground">{label || `Métrica ${idx + 1}`}</Eyebrow>
            <div className="text-3xl font-semibold tabular-nums tracking-tight">
              {value || "—"}
              {unit && <span className="text-base text-muted-foreground ml-1">{unit}</span>}
            </div>
            {(trend || arrow) && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                {arrow && <span className="font-semibold">{arrow}</span>}
                {trend}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function PhaseCardGrid({ items }: { items: unknown[] }) {
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((raw, idx) => {
        const item = asObject(raw);
        const title = asString(item.title);
        const description = asString(item.description);
        const owner = asString(item.owner);
        const duration = asString(item.duration);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: authored content; order is stable
          <Card key={idx} className="p-5 space-y-2">
            <div className="flex items-baseline gap-3">
              <DisplayNumeral index={idx} className="text-2xl" />
              <Eyebrow className="text-muted-foreground">Fase {idx + 1}</Eyebrow>
            </div>
            <div className="text-base font-semibold tracking-tight">
              {title || `Fase ${idx + 1}`}
            </div>
            {description && (
              <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
            )}
            {(owner || duration) && (
              <div className="text-xs text-muted-foreground pt-1 flex gap-4">
                {owner && (
                  <span>
                    <span className="font-semibold">Responsável:</span> {owner}
                  </span>
                )}
                {duration && (
                  <span>
                    <span className="font-semibold">Duração:</span> {duration}
                  </span>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
